import { Button, Col, Divider, Flex, Form, Input, Row, Typography, notification } from 'antd';
import React, { useEffect, useState, useRef } from 'react';
import { BUTTON, TEXT } from '../../../layout/style';

import { useSelector, useDispatch } from 'react-redux';
import { loginStart, loginSuccess, loginFailure, logout } from '../../../redux/slices/authSlice';
import AuthRestAPI from '../../../restAPI/AuthRestAPI';
import validateForm from '../../../utils/FormValidation';
import validationRules from '../../../assets/validation/rules';
import { Link, useNavigate, } from 'react-router-dom';
import { AESEncryption } from '../../../utils/Aes';
import { decodeToken } from "react-jwt";
import { connectNotificationSocket } from '../../../notificationSocketService';
import { MiddlewareClass } from '../../../restAPI/Middleware';
const { Text, Title } = Typography;

export default function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading } = useSelector(state => state.auth);
  const [isReallocationTokenAvailable, setIsReallocationTokenAvailable] = useState(false);
  const [reallocationToken, setReallocationToken] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentValidation, setCurrentValidation] = useState('');
  const [hasNotificationShown, setHasNotificationShown] = useState(false);
  
  // Refs for focus management
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  const [payload, setPayload] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});

  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = (type, message, description, placement) => {
    api[type]({
      message: message,
      description: description,
      placement
    });
  };

  useEffect(() => {
    const sessionExpired = sessionStorage.getItem('sessionExpired');
    console.log('sessionExpired :', sessionExpired)
    if(sessionExpired === 'true'){
      dispatch(logout());
      // Show notification
      openNotificationWithIcon('warning', 'Session Expired', 'Your session has expired. Please log in again.', 'topRight');
      
      // Clear the flag
      sessionStorage.removeItem('sessionExpired');
    }
  }, []);

  useEffect(() => {
    //get reallocation token
    if (window.location.href.includes("/Meeting-Management/alt-reallocation?token=")) {
      const parts = window.location.href.split("?");
      setReallocationToken(parts[1]);
      setIsReallocationTokenAvailable(true);
    }
  }, []);

  function validateFormData(data) {
    const validationResult = validateForm(data, validationRules);
    if (!validationResult.isValid) {
      const err = validationResult.errors;
      const newErrors = {};
      Object.keys(err).forEach((value) => {
        newErrors[value] = err[value];
      });
      setErrors(prev => ({ ...prev, ...newErrors }));
    } else {
      const keyData = Object.keys(data)[0];
      setErrors(prev => ({ ...prev, [keyData]: '' }));
    }
  }

  // Shared validation function for both button click and Enter key
  const validateLoginForm = () => {
    const validateData = {
      loginEmail: payload.email,
      loginPassword: payload.password
    };
    const validationResult = validateForm(validateData, validationRules);

    if (!validationResult.isValid) {
      const err = validationResult.errors;
      const newErrors = {};

      Object.keys(err).forEach((value) => {
        newErrors[value] = err[value];
      });

      setErrors(newErrors);
      return false;
    }
    return true;
  };

  // Handle Enter key press in email field
  const handleEmailKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // If email is valid, focus password field
      if (payload.email && !errors.loginEmail) {
        passwordInputRef.current?.focus();
      } else {
        // If email is invalid, trigger validation and focus email field
        validateLoginForm();
        emailInputRef.current?.focus();
      }
    }
  };

  // Handle Enter key press in password field
  const handlePasswordKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Trigger login if both fields are valid
      if (payload.email && payload.password && !errors.loginEmail && !errors.loginPassword) {
        handleLogin();
      } else {
        // If validation fails, focus the first invalid field
        if (!payload.email || errors.loginEmail) {
          emailInputRef.current?.focus();
        } else if (!payload.password || errors.loginPassword) {
          passwordInputRef.current?.focus();
        }
        // Trigger validation to show errors
        validateLoginForm();
      }
    }
  };

  const handleLogin = async () => {
    // Prevent login if already loading
    if (isLoading) {
      return;
    }

    // Validate form before proceeding
    if (!validateLoginForm()) {
      return;
    }

    setCurrentValidation('');
    setHasNotificationShown(false);
    dispatch(loginStart());
    setIsLoading(true);

      AuthRestAPI.login(payload).then((response) => {
        const res = response.data;
        if (response.status === 200) {
          const decodedTokenValue = decodeToken(res.token);

          //console.log(JSON.stringify(decodedTokenValue));
          if (decodedTokenValue?.companyData.length === 0) {
            openNotificationWithIcon('error', 'Company not assigned', 'You need to be assigned to at least one company!', 'topRight');
            setIsLoading(false);
            return dispatch(loginFailure(`You need to be assigned to at least one company!`));
          }
          if (decodedTokenValue?.companyData.length === 1 && !decodedTokenValue?.companyData[0]?.status) {
            openNotificationWithIcon('error', 'Company not assigned', 'You need to be assigned to at least one company!', 'topRight');
            setIsLoading(false);
            return dispatch(loginFailure(`You need to be assigned to at least one company!`));
          }
          if (decodedTokenValue?.isFirstTime) {
            setIsLoading(false);
            return (navigate("/changePassword", {
              state: {
                token: res.token
              }
            }));
          }

          //check 2fa authentication
          if(decodedTokenValue?.should2faChecked){
             setIsLoading(false);
             return dispatch(navigate("/otp-option", {
              state: {
                token: res.token
              }
             }));
          }

          //check password expiry
          if (decodedTokenValue?.isPasswordExpired) {
            setIsLoading(false);
            openNotificationWithIcon('error', 'Access denied', 'Password has expired. Please reset your password.', 'topRight');
            return (navigate("/changePassword", {
              state: {
                token: res.token
              }
            }));
          }

          const firstCompanyId = decodedTokenValue.companyData[0].companyId;
          const role = decodedTokenValue.companyData[0].roles;
          const userDetailsId = decodedTokenValue.companyData[0]?.userDetailsId;
          
          const payload = {
            token: res.token,
            userCompany: decodedTokenValue,
            companyId: firstCompanyId,
            role: role,            
            userDetailsId: userDetailsId
          };

          console.log('login payload :', payload);

          // if signatory, given someone else's credentials
          if (isReallocationTokenAvailable) {
            const decodedValues = decodeToken(reallocationToken);
            const exists = decodedTokenValue.companyData.some(
              (company) => company.userDetailsId === decodedValues.signatoryId
            );
            if (!exists) {
              openNotificationWithIcon('error', 'Access denied', 'Unauthorized alternative signatory access!', 'topRight');
              return;
            }
          }

          const encryptedToken = AESEncryption(res.token);
          const encryptedCompanyId = AESEncryption((firstCompanyId).toString());


          localStorage.setItem('token', encryptedToken);
          localStorage.setItem('companyId', encryptedCompanyId);

          dispatch(loginSuccess(payload));
          connectNotificationSocket(res.token, firstCompanyId);
          setIsLoading(false);
          openNotificationWithIcon('success', 'Login successful', `Welcome back!`, 'topRight');
          if (isReallocationTokenAvailable) {
            navigate("/Meeting-Management/alt-reallocation?" + `${reallocationToken}`);
          }
          else {
            navigate("/home");
          }
        } else {
          dispatch(loginFailure(response.data));
          openNotificationWithIcon('error', 'Login failed', `${response.data.message}`, 'topRight');
        }
      }).catch((err) => {
        if (err.response) {
          dispatch(loginFailure(err.response.data));
          openNotificationWithIcon('error', 'Login failed', `${err.response.data.message}`, 'topRight');
        } else {
          dispatch(loginFailure(err.message));
          openNotificationWithIcon('error', 'Login failed', `${err.message}`, 'topRight');
        }
      }).finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div>
      <Row>
        <Col>
          <h1 style={TEXT.heading1}>Login</h1>
          <br />
          <Text type='secondary'>
            Welcome back to OCTOPLEX, your all-in-one board meeting and workflow management solution. <br />Log in to continue managing your board meetings and streamline your workflows.
          </Text>
          <br />
          <br />
        </Col>
      </Row>
      <Row>
        <Col xs={{
          span: 20,
        }}
          md={{
            span: 18,
          }}
          lg={{
            span: 16,
          }}>
          <Form name="basic" autoComplete='off'>
            <input type="text" name="fakeusernameremembered" style={{ display: 'none' }} />
            <input type="password" name="fakepasswordremembered" style={{ display: 'none' }} />
            <Form.Item
              validateStatus={errors.loginEmail ? 'error' : ''}
              help={errors.loginEmail || ''}
            >
              <Input
                ref={emailInputRef}
                name='loginEmailFake'
                size="large"
                placeholder="Email"
                autoComplete="off"
                onKeyPress={handleEmailKeyPress}
                onChange={(e) => {
                  const sanitizedEmail = e.target.value.toLowerCase().trim();
                  const validateData = {
                    loginEmail: sanitizedEmail
                  };
                  validateFormData(validateData);
                  setPayload(prev => ({
                    ...prev,
                    email: sanitizedEmail
                  }));
                }}
                value={payload["email"]}
              />
            </Form.Item>
            <Form.Item
              validateStatus={errors.loginPassword ? 'error' : ''}
              help={errors.loginPassword || ''}
            >
              <Input.Password
                ref={passwordInputRef}
                name="loginPasswordFake"
                autoComplete="off"
                size="large"
                placeholder="Password"
                readOnly
                onFocus={(e) => e.target.removeAttribute('readonly')}
                onKeyPress={handlePasswordKeyPress}
                onChange={(e) => {
                  const validateData = {
                    loginPassword: e.target.value
                  };
                  validateFormData(validateData);

                  setPayload(prev => ({
                    ...prev,
                    password: e.target.value
                  }));
                }}
                value={payload["password"]}
              // autoComplete="password"
              />
            </Form.Item>
            <Flex justify='space-between' align='center'>
              {/* <span>
                <Checkbox>Remember me</Checkbox>
              </span> */}
              <a href='./forgetPassword' type="link">Forgot Password</a>
            </Flex>
            <br />
            <Form.Item>
              <Button type="primary" style={BUTTON.primary} onClick={handleLogin} loading={isLoading}>Login</Button>
            </Form.Item>
            <Form.Item hidden>
              <Divider />
              Don't you have an Account ?<Link to='/register'><Button type='link'>Register</Button></Link>
            </Form.Item>
          </Form>
        </Col>
      </Row>
      <Row>
        {contextHolder}
      </Row>
    </div>
  );
}
